import { useState, useEffect } from "react";
import deliveryServiceTypes from "../../../constants/deliveryServiceTypes";
import useStore from "../../../hooks/useStore";
import DoordashV2OnboardingForm from "./DoordashV2OnboardingForm";
import { Button, Col, Row, notification } from "antd";
import styles from "../../deliveryServices/DeliveryServices.module.css";
import getDoordashOnboardingStatus from "../../apiRepository/getDoordashOnboardingStatus";
import resubmitOnboardingForm from "../../apiRepository/resubmitOnboardingForm";
import { CheckCircleFilled } from "@ant-design/icons";
import ProcessingCard from "./cards/ProcessingCard";
import InProgressCard from "./cards/InProgressCard";
import NewMerchantCard from "./cards/NewMerchantCard";
import LoadingCard from "./cards/LoadingCard";
import FailedCard from "./cards/FailedCard";
import DeliverySettings from "../deliverySettings/DeliverySettings";

const transformDateString = (dateString) => {
  const date = new Date(dateString);

  const options = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  };

  return date.toLocaleString("en-US", options);
};

export default function DoordashDS() {
  const { store } = useStore();
  const [showForm, setShowForm] = useState(false);
  const [date, setDate] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasError, setHasError] = useState(false);

  const deliverySetting = store?.deliverySettings?.doordash;

  const isConfigured =
    !!deliverySetting && Object.keys(deliverySetting).length > 0;

  const fetchStatus = async () => {
    setLoading(true);

    try {
      const data = await getDoordashOnboardingStatus({
        storeId: store?.storeId,
        token: store?.token,
      });

      const date = data.onboardingDate ?? data.requestDate;
      setDate(transformDateString(date));
      setStatus(data.status);
    } catch (error) {
      console.error("API error:", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!isConfigured) {
      fetchStatus();
    } else {
      setLoading(false);
    }
  }, [isConfigured]);

  const openNotification = (type) => {
    notification.open({
      message: <strong>{`${type} Request Submitted`}</strong>,
      description: `Your form has been successfully submitted.`,
      placement: "top",
      icon: <CheckCircleFilled style={{ color: "#52c41a" }} />,
    });
  };

  const resubmit = async () => {
    setIsSubmitting(true);
    try {
      await resubmitOnboardingForm({
        storeId: store?.storeId,
        token: store?.token,
      });

      setStatus("ONBOARDING_IN_PROGRESS");
      openNotification("Onboarding");
    } catch (error) {
      setHasError(true);
    }
    setIsSubmitting(false);
  };

  if (!isConfigured && !showForm) {
    let content = (
      <p className={styles.dsFormLabels}>
        Get started by tapping '<b>Connect to DoorDash</b>'' and filling out the
        required form to enable delivery for your store. Make sure all
        information is accurate, as processing can take up to 24 hours or
        longer.
      </p>
    );

    if (status === "PROCESSING_NET_NEW" || status === "PROCESSING") {
      content = <ProcessingCard date={date} />;
    } else if (status === "PROCESSED_NET_NEW") {
      content = <NewMerchantCard date={date} />;
    } else if (status === "ONBOARDING_IN_PROGRESS") {
      content = <InProgressCard date={date} />;
    } else if (status === "ONBOARDING_FAILED") {
      content = <FailedCard date={date} />;
    }

    const getButtonLabel = () => {
      if (status === "PROCESSING") return "PROCESSING...";
      if (status === "PROCESSING_NET_NEW") return "PROCESSING...";
      if (status === "PROCESSED_NET_NEW") return "CONFIRM";
      if (status === "ONBOARDING_IN_PROGRESS") return "PROCESSING...";
      if (status === "ONBOARDING_FAILED") return "RETRY";
      return "CONNECT TO DOORDASH";
    };

    const buttonLabel = getButtonLabel();
    const isConfirm = buttonLabel === "CONFIRM";
    const isRetry = buttonLabel === "RETRY";
    const isProcessing = buttonLabel === "PROCESSING...";

    return (
      <>
        {loading ? (
          <LoadingCard />
        ) : (
          <>
            <Row justify="center" align="middle" style={{ margin: "5% 20%" }}>
              <Col align="middle">
                <div className={styles.dsMainCardStyle}>
                  <img
                    width="50px"
                    height="50px"
                    src="https://bevz-media.s3.us-west-1.amazonaws.com/doordash.png"
                  />
                  <p
                    className={styles.dsFormTitle}
                    style={{ marginBottom: "10px", marginTop: "5px" }}
                  >
                    Connect to DoorDash
                  </p>
                  <div>{content}</div>
                  <br />
                  <Button
                    block
                    type="primary"
                    style={
                      isConfirm
                        ? { backgroundColor: "#34C759" }
                        : isRetry
                        ? { backgroundColor: "#E99E25" }
                        : {}
                    }
                    loading={isConfirm || isRetry ? isSubmitting : false}
                    onClick={
                      isConfirm || isRetry ? resubmit : () => setShowForm(true)
                    }
                    disabled={isProcessing || isSubmitting}
                    className={styles.dsLargeButton}
                  >
                    <p>{isSubmitting ? "" : buttonLabel}</p>
                  </Button>
                </div>
              </Col>
            </Row>
            <Row justify="center" align="middle">
              <Col align="middle">
                {hasError && (
                  <div
                    style={{
                      marginTop: "10px",
                      backgroundColor: "#fff5f5",
                      border: "1px solid #f5c2c7",
                      color: "#000",
                      padding: "16px",
                      borderRadius: "12px",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "12px",
                      width: "420px",
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: "#f25c5c",
                        borderRadius: "50%",
                        width: "30px",
                        height: "17px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        color: "white",
                        fontSize: "10px",
                        fontWeight: "bold",
                      }}
                    >
                      ✕
                    </div>
                    <div style={{ textAlign: "left" }}>
                      <p>Action couldn’t be completed.</p>
                      <p
                        style={{
                          marginTop: "4px",
                          fontSize: "14px",
                          color: "#555",
                        }}
                      >
                        Please check your details, or contact DoorDash Support
                        or Bevz Support at (818) 235-1327 for assistance.
                      </p>
                    </div>
                  </div>
                )}
              </Col>
            </Row>
          </>
        )}
      </>
    );
  }

  if (!isConfigured && showForm) {
    return (
      <DoordashV2OnboardingForm
        closeForm={() => setShowForm(false)}
        onSuccess={() => {
          setStatus("PROCESSING");
          setShowForm(false);
          fetchStatus();
        }}
        deliveryService={deliveryServiceTypes.doordash.capitalize}
      />
    );
  }

  // if configured, show dd delivery settings page
  return <DeliverySettings  deliveryService={deliveryServiceTypes.doordash.cammel} />;
}
